import { type FeatureStandard } from '@solinkcorp/event-standard/lib/src/types';
import memoizeOne from 'memoize-one';
import EventFilterStore from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import { useSelector } from 'stores/store';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import { getAvailableFeatures } from 'utils/FeatureStandardUtils';
import type ReportUtil from 'utils/ReportUtil';
import { type VideoDiscoverySite } from 'utils/types';
import useFluxStore from './useFluxStore';

/**
 * Get a list of report filters that are sorted and filtered by all the locations' available features.
 */
export function useEventReports(): readonly ReportUtil[] | undefined {
  const featureStandard = useFeatureStandard();
  const locations = useFluxStore(LocationStore, (store) => store.getLocations());
  const filterReports = useFluxStore(EventFilterStore, (store) => store.getFilters());
  const localeCode = useSelector((state) => state.localeState.localeCode);

  return featureStandard && locations?.length
    ? getEventReports(featureStandard, locations, filterReports, localeCode)
    : undefined;
}

/**
 * Get a list of report filters that are sorted and filtered by all the locations' available features.
 *
 * @param featureStandard - The feature standard.
 * @param locations - The list of all locations.
 * @param reports - The set of all reports from the EventFilterStore.
 * @param locale - The user's locale (e.g. "en-US").
 */
const getEventReports = memoizeOne(
  (
    featureStandard: FeatureStandard,
    locations: readonly VideoDiscoverySite[],
    filterReports: readonly ReportUtil[],
    locale: string,
  ): ReportUtil[] => {
    const locationsFeatures = getAvailableFeatures(featureStandard, locations);
    const result = filterReports.filter((report) => {
      const categories: string[] = report.metadata?.categories || [];
      return (
        categories.includes('all') ||
        categories.length === 0 ||
        categories.some((category) => locationsFeatures.availableFeatures[category])
      );
    });

    result.sort((report1, report2) => {
      let compareValue;

      const isDefaultReport1 = report1.isDefaultReport();
      const isDefaultReport2 = report2.isDefaultReport();
      const isUserReport1 = report1.isUserReport();
      const isUserReport2 = report2.isUserReport();
      const isCustomerReport1 = report1.isCustomerReport();
      const isCustomerReport2 = report2.isCustomerReport();

      if (isDefaultReport1 && !isDefaultReport2) {
        compareValue = -1;
      } else if (isDefaultReport2 && !isDefaultReport1) {
        compareValue = 1;
      } else if (isUserReport1 && !isUserReport2) {
        compareValue = -1;
      } else if (isUserReport2 && !isUserReport1) {
        compareValue = 1;
      } else if (isCustomerReport1 && !isCustomerReport2) {
        compareValue = -1;
      } else if (isCustomerReport2 && !isCustomerReport1) {
        compareValue = 1;
      } else {
        // Sort alphabetically
        // TODO locale to lower case
        const nameReport1 = report1.getName(locale).toLowerCase();
        const nameReport2 = report2.getName(locale).toLowerCase();

        if (nameReport1 < nameReport2) {
          compareValue = -1;
        } else if (nameReport2 < nameReport1) {
          compareValue = 1;
        } else {
          compareValue = 0;
        }
      }

      return compareValue;
    });

    return result;
  },
);
